<script lang="ts">
import {defineComponent, PropType, toRef, onMounted } from 'vue'
import TextTruncationMultiLine from '@/components/formatter/TextTruncationMultiLine.vue'
import { isValidNumber } from '@/utilities/validator'
import hearingFieldsUtil from '@/models/hearingFieldsUtil'
import userCareer from '@/models/userCareer'
import { formPartial } from '@/models/formPartial'

export default defineComponent({
  name: 'UserLeadInfo',

  components: {
    TextTruncationMultiLine,
  },

  props: {
    userLeadInfo: {
      type: Object as PropType<UserLeadInfo>,
      required: true,
    },

    hearingFieldConfig: {
      type: Object as PropType<HearingFieldConfig | undefined>,
      default: undefined,
    },

    inputHearingFields: {
      type: Object as PropType<Inputs>,
      default: () => ({}),
    },

    namespace: {
      type: String,
      default: '',
    },

    isShowHearingFields: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'reset:corporateZipcode',
    'error',
  ],

  setup(props, { emit }) {
    const { createErrorMessage } = formPartial()
    const { isShowProfessionDomain } = userCareer(toRef(props, 'userLeadInfo'))
    const {
      // computed
      hasHearingFields,
      hasFilledItemsInHearingField,
      filteredHearingFieldSections,

      // methods
      formatHearingFieldsAsString
    } = hearingFieldsUtil(props.hearingFieldConfig)

    onMounted(() => {
      // マウントされた郵便番号の初期値が半角数字以外の文字列が含まれていた場合、強制的にブランクにして更新する
      // ※ 古い会員の場合、まだ半角数字のみのバリデーション制御がなかったため、「-（ハイフン）」つきの郵便番号文字列で登録しているようなユーザもいるため、そういった古いデータは一旦リセットかける
      if (props.userLeadInfo.corporateZipcode && !isValidNumber(props.userLeadInfo.corporateZipcode.value)) {
        emit('reset:corporateZipcode', '')
      }
    })

    return {
      // computed
      hasHearingFields,
      hasFilledItemsInHearingField,
      filteredHearingFieldSections,
      isShowProfessionDomain,

      // methods
      formatHearingFieldsAsString,
      createErrorMessage,
    }
  },
})
</script>

<template>
  <div class="div">
    <div class="o-heading__section">
      <h2 class="o-heading__section--item">
        <slot name="headingLabel">
          下記の情報で間違いないかご確認ください
        </slot>
      </h2>
    <!-- /.o-heading__section --></div>

    <table class="table">
      <colgroup class="colgroup">
        <col class="col is-40p">
        <col class="col">
      </colgroup>
      <tbody class="tbody">
        <!-- 氏名 -->
        <tr
          v-if="userLeadInfo.lastName || userLeadInfo.firstName"
          class="tr">
          <th class="th">
            氏名
          </th>
          <td class="td u-text__bold">
            <div class="l-cols">
              <div
                v-if="userLeadInfo.lastName"
                class="l-cols--col">
                <ValidationProvider
                  :value="userLeadInfo.lastName.value"
                  :namespace="namespace"
                  :field="userLeadInfo.lastName.name"
                  :scheme="userLeadInfo.lastName.validationScheme"
                  :messages="createErrorMessage(false, false, '姓')"
                  @error="$emit('error', $event)">
                  {{ userLeadInfo.lastName.value }}
                </ValidationProvider>
              <!-- /.l-cols col --></div>

              <div
                v-if="userLeadInfo.firstName"
                class="l-cols--col">
                <ValidationProvider
                  :value="userLeadInfo.firstName.value"
                  :namespace="namespace"
                  :field="userLeadInfo.firstName.name"
                  :scheme="userLeadInfo.firstName.validationScheme"
                  :messages="createErrorMessage(false, false, '名')"
                  @error="$emit('error', $event)">
                  {{ userLeadInfo.firstName.value }}
                </ValidationProvider>
              <!-- /.l-cols col --></div>
            <!-- /.l-cols --></div>
          </td>
        </tr>

        <!-- 氏名（かな） -->
        <tr
          v-if="userLeadInfo.lastNameKana || userLeadInfo.firstNameKana"
          class="tr">
          <th class="th">
            氏名（かな）
          </th>
          <td class="td u-text__bold">
            <div class="l-cols">
              <div
                v-if="userLeadInfo.lastNameKana"
                class="l-cols--col">
                <ValidationProvider
                  :value="userLeadInfo.lastNameKana.value"
                  :namespace="namespace"
                  :field="userLeadInfo.lastNameKana.name"
                  :scheme="userLeadInfo.lastNameKana.validationScheme"
                  :messages="createErrorMessage(false, false, '姓（かな）')"
                  @error="$emit('error', $event)">
                  {{ userLeadInfo.lastNameKana.value }}
                </ValidationProvider>
              <!-- /.l-cols col --></div>

              <div
                v-if="userLeadInfo.firstNameKana"
                class="l-cols--col">
                <ValidationProvider
                  :value="userLeadInfo.firstNameKana.value"
                  :namespace="namespace"
                  :field="userLeadInfo.firstNameKana.name"
                  :scheme="userLeadInfo.firstNameKana.validationScheme"
                  :messages="createErrorMessage(false, false, '名（かな）')"
                  @error="$emit('error', $event)">
                  {{ userLeadInfo.firstNameKana.value }}
                </ValidationProvider>
              <!-- /.l-cols col --></div>
            <!-- /.l-cols --></div>
          </td>
        </tr>

        <!-- 勤務先 -->
        <tr
          v-if="userLeadInfo.companyName"
          class="tr">
          <th class="th">
            勤務先
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.companyName.value"
              :namespace="namespace"
              :field="userLeadInfo.companyName.name"
              :scheme="userLeadInfo.companyName.validationScheme"
              :messages="createErrorMessage(false, false, '勤務先')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.companyName.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 業種 -->
        <tr
          v-if="userLeadInfo.industry"
          class="tr">
          <th class="th">
            業種
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.industry.value"
              :namespace="namespace"
              :field="userLeadInfo.industry.name"
              :scheme="userLeadInfo.industry.validationScheme"
              :messages="createErrorMessage(true, false, '業種')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.industry.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 従業員規模 -->
        <tr
          v-if="userLeadInfo.scale"
          class="tr">
          <th class="th">
            従業員規模
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.scale.value"
              :namespace="namespace"
              :field="userLeadInfo.scale.name"
              :scheme="userLeadInfo.scale.validationScheme"
              :messages="createErrorMessage(true, false, '従業員規模')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.scale.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 上場区分 -->
        <tr
          v-if="userLeadInfo.listingStatus && userLeadInfo.listingStatus.name"
          class="tr">
          <th class="th">
            上場区分
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.listingStatus.value"
              :namespace="namespace"
              :field="userLeadInfo.listingStatus.name"
              :scheme="userLeadInfo.listingStatus.validationScheme"
              :messages="createErrorMessage(true, false, '上場区分')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.listingStatus.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 職種 -->
        <tr
          v-if="userLeadInfo.profession"
          class="tr">
          <th class="th">
            職種
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.profession.value"
              :namespace="namespace"
              :field="userLeadInfo.profession.name"
              :scheme="userLeadInfo.profession.validationScheme"
              :messages="createErrorMessage(true, false, '職種')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.profession.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 担当領域 -->
        <tr
          v-if="isShowProfessionDomain && userLeadInfo.professionDomain"
          class="tr">
          <th class="th">
            担当領域
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.professionDomain.value"
              :namespace="namespace"
              :field="userLeadInfo.professionDomain.name"
              :scheme="userLeadInfo.professionDomain.validationScheme"
              :messages="createErrorMessage(false, true, '担当領域')"
              @error="$emit('error', $event)">
              <div class="o-list__cols is-comma">
                <ul class="o-list__cols--items">
                  <li
                    v-for="professionDomain in userLeadInfo.professionDomain.value"
                    :key="professionDomain"
                    class="o-list__cols--item">
                    {{ professionDomain }}
                  </li>
                </ul>
              <!-- /.o-list__cols --></div>
            </ValidationProvider>
          </td>
        </tr>

        <!-- 役職 -->
        <tr
          v-if="userLeadInfo.positionLevel"
          class="tr">
          <th class="th">
            役職
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.positionLevel.value"
              :namespace="namespace"
              :field="userLeadInfo.positionLevel.name"
              :scheme="userLeadInfo.positionLevel.validationScheme"
              :messages="createErrorMessage(true, false, '役職')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.positionLevel.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 部署・役職正式名称 -->
        <tr
          v-if="userLeadInfo.departmentAndPosition"
          class="tr">
          <th class="th">
            部署・役職正式名称
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.departmentAndPosition.value"
              :namespace="namespace"
              :field="userLeadInfo.departmentAndPosition.name"
              :scheme="userLeadInfo.departmentAndPosition.validationScheme"
              :messages="createErrorMessage(false, false, '部署・役職正式名称')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.departmentAndPosition.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 勤務先メールアドレス -->
        <tr
          v-if="userLeadInfo.workMailAddress"
          class="tr">
          <th class="th">
            勤務先メールアドレス
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.workMailAddress.value"
              :namespace="namespace"
              :field="userLeadInfo.workMailAddress.name"
              :scheme="userLeadInfo.workMailAddress.validationScheme"
              :messages="createErrorMessage(false, false, '勤務先メールアドレス')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.workMailAddress.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!-- 電話番号 -->
        <tr
          v-if="userLeadInfo.phoneNumber"
          class="tr">
          <th class="th">
            電話番号
          </th>
          <td class="td u-text__bold">
            <ValidationProvider
              :value="userLeadInfo.phoneNumber.value"
              :namespace="namespace"
              :field="userLeadInfo.phoneNumber.name"
              :scheme="userLeadInfo.phoneNumber.validationScheme"
              :messages="createErrorMessage(false, false, '電話番号')"
              @error="$emit('error', $event)">
              {{ userLeadInfo.phoneNumber.value }}
            </ValidationProvider>
          </td>
        </tr>

        <!--
          ヒアリング項目
          回答済み項目が一つでもある場合表示
        -->
        <template v-if="hasFilledItemsInHearingField && isShowHearingFields">
          <tr
            v-for="section in filteredHearingFieldSections"
            :key="section.id"
            class="tr">
            <th class="th">
              {{ section.title }}
            </th>
            <td class="td u-text__bold">
              <TextTruncationMultiLine>
                {{ formatHearingFieldsAsString(section.items) }}
              </TextTruncationMultiLine>

              <template
                v-for="item in section.items"
                :key="item.hearingFieldId">
                <ValidationProvider
                  v-if="inputHearingFields[`${item.name}`]"
                  :value="inputHearingFields[`${item.name}`].value"
                  :namespace="namespace"
                  :field="inputHearingFields[`${item.name}`].name"
                  :scheme="inputHearingFields[`${item.name}`].validationScheme"
                  @error="$emit('error', $event)" />

                <ValidationProvider
                  v-if="inputHearingFields[`${item.name}${hearingFieldConfig!.nameSuffix.secondaryValue}`]"
                  :value="inputHearingFields[`${item.name}${hearingFieldConfig!.nameSuffix.secondaryValue}`].value"
                  :namespace="namespace"
                  :field="inputHearingFields[`${item.name}${hearingFieldConfig!.nameSuffix.secondaryValue}`].name"
                  :scheme="inputHearingFields[`${item.name}${hearingFieldConfig!.nameSuffix.secondaryValue}`].validationScheme"
                  @error="$emit('error', $event)" />
              </template>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <ValidationProvider
      :value="userLeadInfo.corporateZipcode?.value"
      :namespace="namespace"
      :field="userLeadInfo.corporateZipcode?.name"
      :scheme="userLeadInfo.corporateZipcode?.validationScheme"
      :messages="createErrorMessage(false, false, '会社郵便番号')"
      @error="$emit('error', $event)" />

    <ValidationProvider
      v-if="userLeadInfo.corporateAddress1"
      :value="userLeadInfo.corporateAddress1.value"
      :namespace="namespace"
      :field="userLeadInfo.corporateAddress1.name"
      :scheme="userLeadInfo.corporateAddress1.validationScheme"
      :messages="createErrorMessage(false, false, '会社住所 1')"
      @error="$emit('error', $event)" />

    <ValidationProvider
      v-if="userLeadInfo.corporateAddress2"
      :value="userLeadInfo.corporateAddress2.value"
      :namespace="namespace"
      :field="userLeadInfo.corporateAddress2.name"
      :scheme="userLeadInfo.corporateAddress2.validationScheme"
      :messages="createErrorMessage(false, false, '会社住所 2')"
      @error="$emit('error', $event)" />

    <ValidationProvider
      v-if="userLeadInfo.corporateNumber"
      :value="userLeadInfo.corporateNumber.value"
      :namespace="namespace"
      :field="userLeadInfo.corporateNumber.name"
      :scheme="userLeadInfo.corporateNumber.validationScheme"
      :messages="createErrorMessage(false, false, '法人番号')"
      @error="$emit('error', $event)" />

    <ValidationProvider
      v-if="userLeadInfo.corporateEmployeeNum"
      :value="userLeadInfo.corporateEmployeeNum.value"
      :namespace="namespace"
      :field="userLeadInfo.corporateEmployeeNum.name"
      :scheme="userLeadInfo.corporateEmployeeNum.validationScheme"
      :messages="createErrorMessage(false, false, '従業員数')"
      @error="$emit('error', $event)" />

    <input
      class="input"
      type="hidden"
      name="simple"
      value="true">

    <div class="o-note__general u-text__small">
      <p class="o-note__general--item">正確な情報のご入力をお願いいたします。虚偽や不正があった場合は、サービスの利用を停止させていただく場合があります。</p>
    <!-- /.o-note__general --></div>
  </div>
</template>

<style lang="sass" scoped>
// object
.o-heading__section
  & &--item
    font:
      size: $font-normal
      weight: bold
    text-align: center
</style>
